/**
 * Page Targeting / suppress-list patterns.
 *
 * The embed script compares these against `window.location.pathname`. An absolute URL
 * therefore matches nothing — pasting the address bar into "show only on these pages"
 * silently produces a bot that renders on zero pages, including the page it names.
 *
 * The server normalizes on write and again at embed generation; this mirrors it so the
 * field shows the user what will actually be matched instead of accepting input that
 * quietly does nothing. Keep the two in step —
 * `mb-server/mb-backend/src/utils/routePatterns.js` is the authority.
 */

const ABSOLUTE_URL_RE = /^([a-z][a-z0-9+.-]*:)?\/\//i;
const BARE_HOST_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?(\/|$)/i;

/**
 * Reduce one pattern to a pathname. Returns '' when nothing usable remains.
 * The `*` wildcard is preserved verbatim — the embed strips it before comparing.
 */
export const normalizeRoutePattern = (raw: string): string => {
  let value = (raw ?? '').trim();
  if (!value) return '';
  if (value === '*') return '*';

  if (ABSOLUTE_URL_RE.test(value)) {
    const withScheme = value.startsWith('//') ? `https:${value}` : value;
    try {
      value = new URL(withScheme).pathname;
    } catch {
      value = value.replace(ABSOLUTE_URL_RE, '');
      const slash = value.indexOf('/');
      value = slash === -1 ? '/' : value.slice(slash);
    }
  } else if (BARE_HOST_RE.test(value)) {
    const slash = value.indexOf('/');
    value = slash === -1 ? '/' : value.slice(slash);
  }

  value = value.split('#')[0].split('?')[0].trim();
  if (!value) return '/';
  return value.startsWith('/') ? value : `/${value}`;
};

/** Normalize a list, dropping empties and collapsing duplicates while keeping order. */
export const normalizeRoutePatterns = (values: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const pattern = normalizeRoutePattern(value);
    if (!pattern || seen.has(pattern)) continue;
    seen.add(pattern);
    out.push(pattern);
  }
  return out;
};
